import apiClient from 'core/api/client';
import {
  assertImageFileSize,
  multipartRequestConfig,
  pollUploadJob,
} from 'shared/lib/uploadJob';
import type {
  CreateVehicleBody,
  DocumentType,
  SubmitVerificationBody,
  VehicleListItem,
  VehicleResponse,
  VehicleVerificationUploadEnqueueResponse,
  VehicleVerificationUploadJobResponse,
} from 'types/api.generated';

export type { VehicleListItem, VehicleResponse };

export const listMyVehicles = async (): Promise<VehicleListItem[]> => {
  const { data } = await apiClient.get<VehicleListItem[]>('/api/v1/vehicles/mine');
  return data;
};

export const getVehicle = async (vehicleId: string): Promise<VehicleResponse> => {
  const { data } = await apiClient.get<VehicleResponse>(`/api/v1/vehicles/${vehicleId}`);
  return data;
};

export const createVehicle = async (body: CreateVehicleBody): Promise<VehicleResponse> => {
  const { data } = await apiClient.post<VehicleResponse>('/api/v1/vehicles', body);
  return data;
};

export const submitVerification = async (
  vehicleId: string,
  body: SubmitVerificationBody,
): Promise<VehicleResponse> => {
  const { data } = await apiClient.post<VehicleResponse>(
    `/api/v1/vehicles/${vehicleId}/verification`,
    body,
  );
  return data;
};

export const enqueueVehicleVerificationDocument = async (
  vehicleId: string,
  file: File,
  documentType: DocumentType,
  orgId: string,
): Promise<VehicleVerificationUploadEnqueueResponse> => {
  assertImageFileSize(file);
  const form = new FormData();
  form.append('image', file);
  form.append('documentType', documentType);

  const { data } = await apiClient.post<VehicleVerificationUploadEnqueueResponse>(
    `/api/v1/vehicles/${vehicleId}/verification/documents`,
    form,
    {
      ...multipartRequestConfig,
      headers: {
        ...multipartRequestConfig.headers,
        'X-Org-Id': orgId,
      },
    },
  );
  return data;
};

export const getVehicleVerificationUploadJob = async (
  vehicleId: string,
  jobId: string,
  orgId: string,
): Promise<VehicleVerificationUploadJobResponse> => {
  const { data } = await apiClient.get<VehicleVerificationUploadJobResponse>(
    `/api/v1/vehicles/${vehicleId}/verification/upload-jobs/${jobId}`,
    { headers: { 'X-Org-Id': orgId } },
  );
  return data;
};

export const uploadVehicleVerificationDocumentAndWait = async (
  vehicleId: string,
  file: File,
  documentType: DocumentType,
  orgId: string,
): Promise<string> => {
  const { jobId } = await enqueueVehicleVerificationDocument(
    vehicleId,
    file,
    documentType,
    orgId,
  );
  return pollUploadJob(async () => {
    const job = await getVehicleVerificationUploadJob(vehicleId, jobId, orgId);
    return { status: job.status, url: job.url, error: job.error };
  });
};

